import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import { DashboardStatsSkeleton, SessionListSkeleton } from '../../components/ui/LoadingSkeleton';
import { CheckCircle2, Sparkles, Zap, Award, Star, Clock } from 'lucide-react';
import { participantService, authService } from '../../services';
import { usePreferences } from '../../contexts/PreferencesContext';

/**
 * Participant Dashboard
 * Clean, minimal interface showing participant's sessions and quick actions
 * Follows Slido-inspired design principles
 */

const ParticipantDashboard = () => {
  const navigate = useNavigate();
  const { formatDateWithTimezone, formatTime } = usePreferences();
  const [loading, setLoading] = useState(true);
  const [participant, setParticipant] = useState(null);
  const [stats, setStats] = useState({
    totalCompleted: 0,
    certificatesEarned: 0,
    averageScore: 0,
    totalHours: 0,
    avgEngagementScore: 0,
    totalActivitiesCompleted: 0,
  });
  const [completedSessions, setCompletedSessions] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);

  useEffect(() => {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      // Redirect to join page for unauthenticated users
      navigate('/join', { replace: true });
      return;
    }

    // Get participant from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setParticipant(user);
    }

    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await participantService.getDashboardStats();

      if (response.success && response.data) {
        const data = response.data;

        // Set stats
        setStats({
          totalCompleted: data.stats?.completedSessions || 0,
          certificatesEarned: data.stats?.certificates || 0,
          averageScore: data.stats?.averageScore || 0,
          totalHours: data.stats?.totalHours || 0,
          avgEngagementScore: data.stats?.avgEngagementScore || 0,
          totalActivitiesCompleted: data.stats?.totalActivitiesCompleted || 0,
        });

        // Set active session if any
        setActiveSession(data.activeSession || null);

        // Set upcoming sessions
        setUpcomingSessions(
          (data.upcomingSessions || []).map((s) => ({
            id: s.id,
            name: s.name,
            date: s.scheduledDate,
            startTime: s.startTime,
            trainerName: s.trainerName,
            code: s.code,
            status: 'scheduled',
          }))
        );

        // Set recent/completed sessions - use data directly from API
        setCompletedSessions(
          (data.recentSessions || [])
            .filter((s) => s.status === 'completed')
            .map((s) => ({
              id: s.id,
              name: s.name,
              date: s.scheduledDate,
              organizationName: s.organizationName,
              trainerName: s.trainerName,
              duration: s.duration ? `${Math.round(s.duration / 60)}h` : '2h',
              score: s.score || 0,
              activitiesCompleted: s.activitiesCompleted || 0,
              totalActivities: s.totalActivities || 0,
              launchedActivities: s.launchedActivities || s.totalActivities || 0,
              certificateEarned: s.certificateEarned || s.status === 'completed',
            }))
        );
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // If unauthorized, redirect to login
      if (error.message?.includes('Not authorized') || error.message?.includes('401')) {
        authService.logout();
        navigate('/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBadgeVariant = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'info';
    if (score >= 50) return 'warning';
    return 'error';
  };

  return (
    <DashboardLayout title="My Dashboard">
      <div className="p-6 space-y-6">
        {/* Active Session Banner */}
        {activeSession && (
          <Card padding="md" className="bg-green-50 border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <div>
                  <p className="font-semibold text-green-900">Live Session: {activeSession.name}</p>
                  <p className="text-sm text-green-700">Session code: {activeSession.code}</p>
                </div>
              </div>
              <Button variant="primary" size="sm" onClick={() => navigate(`/session/${activeSession.id}/lobby`)}>
                Join Now
              </Button>
            </div>
          </Card>
        )}

        {/* Welcome Section - Clean and Simple */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-neutral-900">
            Welcome back, {participant?.firstName || participant?.name || 'Participant'}
          </h1>
          <p className="text-sm text-neutral-600 mt-1">Here's your learning journey overview</p>
        </div>

        {/* Stats Cards - Minimal Design */}
        {loading ? (
          <DashboardStatsSkeleton count={6} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard label="Completed Sessions"   value={stats.totalCompleted}                  icon={CheckCircle2} variant="emerald" />
            <StatCard label="Activities Completed" value={stats.totalActivitiesCompleted}        icon={Sparkles}     variant="indigo" />
            <StatCard label="Engagement Score"     value={`${stats.avgEngagementScore}%`}        icon={Zap}          variant="primary" />
            <StatCard label="Certificates"         value={stats.certificatesEarned}              icon={Award}        variant="amber" />
            <StatCard label="Average Score"        value={`${stats.averageScore}%`}              icon={Star}         variant="violet" />
            <StatCard label="Total Hours"          value={`${stats.totalHours}h`}                icon={Clock}        variant="blue" />
          </div>
        )}

        {/* Quick Actions - Simplified */}
        <Card padding="md" className="border border-neutral-200">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="md"
              onClick={() => navigate('/participant/certificates')}
            >
              View Certificates ({stats.certificatesEarned})
            </Button>

            <Button
              variant="outline"
              size="md"
              onClick={() => navigate('/participant/activity-history')}
            >
              Activity History
            </Button>

            <Button
              variant="primary"
              size="md"
              onClick={() => navigate('/join')}
            >
              Join New Session
            </Button>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Upcoming Sessions */}
          <Card padding="lg" className="border border-neutral-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-neutral-900">Upcoming Sessions</h2>
              <Badge variant="info" size="sm">{upcomingSessions.length}</Badge>
            </div>

            {loading ? (
              <SessionListSkeleton count={2} />
            ) : upcomingSessions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-neutral-500 text-sm mb-3">No upcoming sessions</p>
                <Button variant="outline" size="sm" onClick={() => navigate('/join')}>
                  Join a Session
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingSessions.map((session) => (
                  <div
                    key={session.id}
                    className="p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/session/${session.id}/lobby`)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-sm text-neutral-900">{session.name}</h3>
                    </div>
                    <div className="space-y-1 text-xs text-neutral-600">
                      <p>{formatDateWithTimezone(session.date, { day: 'numeric', month: 'short', year: 'numeric' })} at {session.startTime ? formatTime(session.startTime) : ''}</p>
                      <p>{session.organizationName} • Trainer: {session.trainerName}</p>
                      <div className="mt-2 pt-2 border-t border-neutral-200">
                        <span className="font-mono bg-neutral-100 px-2 py-1 rounded">
                          #{session.code}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent Completed Sessions */}
          <Card padding="lg" className="border border-neutral-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-neutral-900">Completed Sessions</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/participant/activity-history')}
              >
                View All
              </Button>
            </div>

            {loading ? (
              <SessionListSkeleton count={3} />
            ) : completedSessions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-neutral-500 text-sm">No completed sessions yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {completedSessions.slice(0, 3).map((session) => (
                  <div
                    key={session.id}
                    className="p-3 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/participant/certificates/${session.id}`)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-sm text-neutral-900">{session.name}</h3>
                      <Badge variant={getScoreBadgeVariant(session.score)} size="sm">
                        {session.score}%
                      </Badge>
                    </div>
                    <div className="space-y-1 text-xs text-neutral-600">
                      <p>{formatDateWithTimezone(session.date, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      <p>{session.activitiesCompleted}/{session.launchedActivities || session.totalActivities} activities • {session.duration}</p>
                      {session.certificateEarned && (
                        <p className="text-amber-600 font-medium">Certificate Available</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Performance Insight - Simplified */}
        {completedSessions.length > 0 && (
          <Card padding="md" className="bg-blue-50 border border-blue-200">
            <p className="text-sm text-neutral-700">
              {stats.averageScore >= 90 ? (
                <>Excellent work! Average score: <strong>{stats.averageScore}%</strong></>
              ) : stats.averageScore >= 70 ? (
                <>Great job! Average score: <strong>{stats.averageScore}%</strong></>
              ) : (
                <>Keep learning! Average score: <strong>{stats.averageScore}%</strong></>
              )}
            </p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ParticipantDashboard;
